import { useSearchParams } from 'react-router-dom'
import { Icon } from '../components/Icon'
import { useMe } from '../hooks/useMe'
import { EventsOverviewTab } from '../components/calendar/EventsOverviewTab'
import { ProgrammingOverviewTab } from '../components/calendar/ProgrammingOverviewTab'

// Calendar — tabbed shell (spec 41). Two tabs share a year stepper:
//   1. Programming overview (default) — the editable annual planner (tab 1).
//   2. Events overview — the read-only real-events grid (spec 40, tab 2).
// Tab + year are URL-serialised (?tab=programme|events & ?year=YYYY) so links
// and refreshes land on the same view.

type TabKey = 'programme' | 'events'

export function Calendar() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: me } = useMe()
  const isAdmin = !!me?.isAdmin

  const yearParam = Number(searchParams.get('year'))
  const year = Number.isFinite(yearParam) && yearParam > 1900 ? yearParam : new Date().getFullYear()
  const tab: TabKey = searchParams.get('tab') === 'events' ? 'events' : 'programme'

  function setYear(next: number) {
    const p = new URLSearchParams(searchParams)
    p.set('year', String(next))
    setSearchParams(p)
  }
  function setTab(next: TabKey) {
    const p = new URLSearchParams(searchParams)
    p.set('tab', next)
    setSearchParams(p, { replace: true })
  }

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl mb-1">Calendar</h1>
          <p className="text-ink-soft">The {year} programme at a glance.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setYear(year - 1)}
            className="px-2.5 py-1.5 rounded text-sm font-semibold border border-line text-ink-soft hover:text-green hover:border-green"
            aria-label="Previous year"
          >
            <Icon name="chevronLeft" size={13} />
          </button>
          <span className="font-display text-xl px-2 min-w-[3.5rem] text-center">{year}</span>
          <button
            type="button"
            onClick={() => setYear(year + 1)}
            className="px-2.5 py-1.5 rounded text-sm font-semibold border border-line text-ink-soft hover:text-green hover:border-green"
            aria-label="Next year"
          >
            <Icon name="chevronRight" size={13} />
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1 border-b border-line" role="tablist">
        <TabButton active={tab === 'programme'} onClick={() => setTab('programme')}>Programming overview</TabButton>
        <TabButton active={tab === 'events'} onClick={() => setTab('events')}>Events overview</TabButton>
      </div>

      {tab === 'programme'
        ? <ProgrammingOverviewTab year={year} isAdmin={isAdmin} />
        : <EventsOverviewTab year={year} />}
    </div>
  )
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={
        'px-4 py-2 text-sm font-semibold -mb-px border-b-2 transition-colors ' +
        (active ? 'border-green text-green' : 'border-transparent text-ink-soft hover:text-green')
      }
    >
      {children}
    </button>
  )
}
